import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { theme, cardStyle, inputStyle, btnStyle, labelStyle, tdStyle, thStyle, statCard } from '../theme';
import WaitListPage from './WaitListPage';

// `wait-list` legacy hub key opens OnlineBookingsPage on the Wait List tab.
const LEGACY_TAB_MAP = { 'wait-list': 'waitList' };

const OnlineBookingsPage = ({ API_BASE, initialTab = 'queue' }) => {
  const [tab, setTab] = useState(LEGACY_TAB_MAP[initialTab] || initialTab); // queue | confirmed | rejected | all | waitList
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);

  // Reject dialog
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  // Counter-propose dialog
  const [counterTarget, setCounterTarget] = useState(null);
  const [cDate, setCDate] = useState('');
  const [cTime, setCTime] = useState('');
  const [cReason, setCReason] = useState('');
  const [busy, setBusy] = useState(false);

  // Server returns the queue pre-sorted (priority desc → newest first), so we
  // never re-sort client-side. For the "queue" tab we fetch both pending +
  // counter_pending so staff sees the full backlog (counter ones are read-only
  // until the patient responds).
  const fetchBookings = async (mode) => {
    setLoading(true);
    try {
      if (mode === 'queue') {
        const [a, b] = await Promise.all([
          axios.get(`${API_BASE}/portal/bookings?status=pending`),
          axios.get(`${API_BASE}/portal/bookings?status=counter_pending`),
        ]);
        setBookings([...(a.data || []), ...(b.data || [])]);
      } else if (mode === 'all') {
        const r = await axios.get(`${API_BASE}/portal/bookings`);
        setBookings(r.data || []);
      } else {
        const r = await axios.get(`${API_BASE}/portal/bookings?status=${mode}`);
        setBookings(r.data || []);
      }
    } catch (e) { setBookings([]); }
    setLoading(false);
  };

  useEffect(() => { fetchBookings('queue'); }, []); // eslint-disable-line

  const handleTab = (t) => { setTab(t); if (t !== 'waitList') fetchBookings(t); };

  const handleConfirm = async (id) => {
    setBusy(true);
    try {
      await axios.put(`${API_BASE}/portal/bookings/${id}/confirm`);
      fetchBookings(tab);
    } catch(e){ alert('Confirm failed: ' + (e.response?.data?.error || e.message)); }
    setBusy(false);
  };

  const submitReject = async () => {
    if (!rejectTarget) return;
    setBusy(true);
    try {
      await axios.put(`${API_BASE}/portal/bookings/${rejectTarget._id}/reject`, { reason: rejectReason });
      setRejectTarget(null); setRejectReason('');
      fetchBookings(tab);
    } catch(e){ alert('Reject failed: ' + (e.response?.data?.error || e.message)); }
    setBusy(false);
  };

  const submitCounter = async () => {
    if (!counterTarget || !cDate) return alert('Date select karein');
    setBusy(true);
    try {
      await axios.put(`${API_BASE}/portal/bookings/${counterTarget._id}/counter`, {
        counterDate: cDate, counterTime: cTime, reason: cReason,
      });
      setCounterTarget(null); setCDate(''); setCTime(''); setCReason('');
      fetchBookings(tab);
    } catch(e){ alert('Counter failed: ' + (e.response?.data?.error || e.message)); }
    setBusy(false);
  };

  const fmtDateTime = d => d ? new Date(d).toLocaleString('en-IN',{day:'2-digit',month:'short',hour:'2-digit',minute:'2-digit'}) : '—';

  const statusStyles = {
    pending:         { bg:'#fef3c7', color:'#92400e', label:'PENDING' },
    counter_pending: { bg:'#dbeafe', color:'#1e40af', label:'AWAITING PATIENT' },
    confirmed:       { bg:'#d1fae5', color:'#065f46', label:'CONFIRMED' },
    rejected:        { bg:'#fee2e2', color:'#991b1b', label:'REJECTED' },
    cancelled:       { bg:'#e5e7eb', color:'#374151', label:'CANCELLED' },
  };

  const kindMeta = {
    new:        { label:'NEW',        bg:theme.primary+'18', color:theme.primary, icon:'➕' },
    reschedule: { label:'RESCHEDULE', bg:'#fef3c7',          color:'#92400e',     icon:'🔄' },
    cancel:     { label:'CANCEL',     bg:'#fee2e2',          color:'#991b1b',     icon:'❌' },
  };

  const queueCount = bookings.filter(b => b.status === 'pending').length;
  const counterCount = bookings.filter(b => b.status === 'counter_pending').length;
  const urgentCount = bookings.filter(b => (b.kind === 'cancel' || b.kind === 'reschedule') && b.status === 'pending').length;

  const tabBtn = (key, label, badge) => (
    <button onClick={()=>handleTab(key)}
      style={{padding:'9px 20px', borderRadius:'8px', border:'none', cursor:'pointer', fontWeight:'600', fontSize:'0.86rem',
        backgroundColor: tab===key ? theme.primary : '#e9ecef', color: tab===key ? 'white' : '#555'}}>
      {label}
      {badge > 0 && <span style={{marginLeft:'6px', padding:'1px 8px', borderRadius:'10px', fontSize:'0.72rem', fontWeight:'700',
        backgroundColor: tab===key ? 'rgba(255,255,255,0.3)' : theme.danger+'20', color: tab===key ? 'white' : theme.danger}}>{badge}</span>}
    </button>
  );

  return (
    <div>
      <div style={{display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:'16px', marginBottom:'20px'}}>
        <div style={statCard}>
          <span style={{fontSize:'0.78rem', color:theme.textMuted, fontWeight:'600', textTransform:'uppercase'}}>Pending Approval</span>
          <h2 style={{margin:'4px 0 0', color:theme.warning, fontSize:'1.5rem', fontWeight:'700'}}>{queueCount}</h2>
        </div>
        <div style={statCard}>
          <span style={{fontSize:'0.78rem', color:theme.textMuted, fontWeight:'600', textTransform:'uppercase'}}>🚨 Urgent (Cancel/Resched)</span>
          <h2 style={{margin:'4px 0 0', color:theme.danger, fontSize:'1.5rem', fontWeight:'700'}}>{urgentCount}</h2>
        </div>
        <div style={statCard}>
          <span style={{fontSize:'0.78rem', color:theme.textMuted, fontWeight:'600', textTransform:'uppercase'}}>Awaiting Patient</span>
          <h2 style={{margin:'4px 0 0', color:theme.primary, fontSize:'1.5rem', fontWeight:'700'}}>{counterCount}</h2>
        </div>
        <div style={statCard}>
          <span style={{fontSize:'0.78rem', color:theme.textMuted, fontWeight:'600', textTransform:'uppercase'}}>Total Listed</span>
          <h2 style={{margin:'4px 0 0', color:theme.navy, fontSize:'1.5rem', fontWeight:'700'}}>{bookings.length}</h2>
        </div>
      </div>

      <div style={{display:'flex', gap:'8px', marginBottom:'20px'}}>
        {tabBtn('queue',     'Action Queue', queueCount + counterCount)}
        {tabBtn('confirmed', 'Confirmed', 0)}
        {tabBtn('rejected',  'Rejected', 0)}
        {tabBtn('all',       'All', 0)}
        {tabBtn('waitList',  'Wait List', 0)}
      </div>

      {/* WAIT LIST — embedded as a tab */}
      {tab === 'waitList' && <WaitListPage API_BASE={API_BASE} />}

      {tab !== 'waitList' && <div style={cardStyle}>
        {loading ? <p style={{color:theme.textMuted}}>Loading...</p> :
         bookings.length === 0 ? (
          <div style={{textAlign:'center', padding:'50px', color:'#bbb'}}>No bookings in this tab</div>
        ) : (
          <table style={{width:'100%', borderCollapse:'collapse'}}>
            <thead><tr>
              <th style={thStyle}>Kind</th>
              <th style={thStyle}>Requested At</th>
              <th style={thStyle}>Patient</th>
              <th style={thStyle}>Phone</th>
              <th style={thStyle}>Slot Requested</th>
              <th style={thStyle}>Patient Note</th>
              <th style={thStyle}>Status</th>
              <th style={thStyle}>Actions</th>
            </tr></thead>
            <tbody>{bookings.map(b => {
              const ss = statusStyles[b.status] || statusStyles.pending;
              const km = kindMeta[b.kind] || kindMeta.new;
              const isUrgent = (b.kind === 'cancel' || b.kind === 'reschedule') && b.status === 'pending';
              return (
                <tr key={b._id} style={isUrgent ? {backgroundColor:'#fff7ed'} : {}}>
                  <td style={tdStyle}>
                    <span style={{padding:'3px 9px', borderRadius:'12px', fontSize:'0.74rem', fontWeight:'700', backgroundColor:km.bg, color:km.color}}>
                      {km.icon} {km.label}
                    </span>
                  </td>
                  <td style={tdStyle}>{fmtDateTime(b.createdAt)}</td>
                  <td style={tdStyle}><strong>{b.patientName}</strong></td>
                  <td style={tdStyle}>{b.phone}</td>
                  <td style={tdStyle}>
                    <div>{b.preferredDate} <span style={{color:theme.textMuted}}>· {b.preferredTime || '—'}</span></div>
                    {b.status === 'counter_pending' && (
                      <div style={{marginTop:4, padding:'4px 8px', backgroundColor:'#dbeafe', borderRadius:6, fontSize:'0.78rem'}}>
                        ↪ You proposed: <strong>{b.counterDate} {b.counterTime}</strong>
                      </div>
                    )}
                  </td>
                  <td style={tdStyle}><em style={{color:theme.textMuted, fontSize:'0.8rem'}}>{b.patientReason || '—'}</em></td>
                  <td style={tdStyle}>
                    <span style={{padding:'3px 10px', borderRadius:'12px', fontSize:'0.74rem', fontWeight:'700', backgroundColor:ss.bg, color:ss.color}}>{ss.label}</span>
                    {b.status === 'rejected' && b.rejectReason && (
                      <div style={{fontSize:'0.7rem', color:theme.danger, marginTop:3}}>"{b.rejectReason}"</div>
                    )}
                  </td>
                  <td style={tdStyle}>
                    {b.status === 'pending' && (
                      <div style={{display:'flex', gap:'6px', flexWrap:'wrap'}}>
                        <button onClick={()=>handleConfirm(b._id)} disabled={busy}
                          style={{padding:'4px 10px', backgroundColor:theme.success, color:'white', border:'none', borderRadius:'6px', cursor:'pointer', fontSize:'0.74rem', fontWeight:'600'}}>
                          ✓ Approve
                        </button>
                        {b.kind !== 'cancel' && (
                          <button onClick={()=>{ setCounterTarget(b); setCDate(b.preferredDate || ''); setCTime(b.preferredTime || ''); }} disabled={busy}
                            style={{padding:'4px 10px', backgroundColor:theme.primary, color:'white', border:'none', borderRadius:'6px', cursor:'pointer', fontSize:'0.74rem', fontWeight:'600'}}>
                            ↔ Counter
                          </button>
                        )}
                        <button onClick={()=>setRejectTarget(b)} disabled={busy}
                          style={{padding:'4px 10px', backgroundColor:theme.danger, color:'white', border:'none', borderRadius:'6px', cursor:'pointer', fontSize:'0.74rem', fontWeight:'600'}}>
                          ✗ Reject
                        </button>
                      </div>
                    )}
                    {b.status === 'counter_pending' && (
                      <em style={{color:theme.primary, fontSize:'0.78rem'}}>Waiting for patient response…</em>
                    )}
                  </td>
                </tr>
              );
            })}</tbody>
          </table>
        )}
      </div>}

      {/* Reject reason dialog */}
      {rejectTarget && (
        <div onClick={()=>setRejectTarget(null)} style={{position:'fixed', inset:0, backgroundColor:'rgba(0,0,0,0.45)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000}}>
          <div onClick={e=>e.stopPropagation()} style={{backgroundColor:'white', padding:'24px', borderRadius:'12px', width:'90%', maxWidth:'440px'}}>
            <h3 style={{marginTop:0}}>Reject Request</h3>
            <p style={{fontSize:'0.85rem', color:theme.textMuted}}>Reason patient ko notification me dikhega.</p>
            <label style={labelStyle}>Reason</label>
            <textarea value={rejectReason} onChange={e=>setRejectReason(e.target.value)} placeholder="e.g. Doctor uss din chhutti pe hain"
              style={{...inputStyle, minHeight:'80px', resize:'vertical'}}/>
            <div style={{display:'flex', gap:'8px', marginTop:'14px', justifyContent:'flex-end'}}>
              <button onClick={()=>setRejectTarget(null)} style={{...btnStyle, backgroundColor:'#e5e7eb', color:'#333'}}>Cancel</button>
              <button onClick={submitReject} disabled={busy} style={{...btnStyle, backgroundColor:theme.danger}}>
                {busy ? 'Sending…' : 'Send Rejection'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Counter-propose dialog */}
      {counterTarget && (
        <div onClick={()=>setCounterTarget(null)} style={{position:'fixed', inset:0, backgroundColor:'rgba(0,0,0,0.45)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000}}>
          <div onClick={e=>e.stopPropagation()} style={{backgroundColor:'white', padding:'24px', borderRadius:'12px', width:'90%', maxWidth:'440px'}}>
            <h3 style={{marginTop:0}}>Counter-propose New Slot</h3>
            <p style={{fontSize:'0.85rem', color:theme.textMuted}}>Patient ko notification jayega — accept/decline kar sakta hai.</p>
            <label style={labelStyle}>New Date</label>
            <input type="date" value={cDate} onChange={e=>setCDate(e.target.value)} style={inputStyle}/>
            <label style={labelStyle}>New Time</label>
            <input type="time" value={cTime} onChange={e=>setCTime(e.target.value)} style={inputStyle}/>
            <label style={labelStyle}>Reason / Note (optional)</label>
            <textarea value={cReason} onChange={e=>setCReason(e.target.value)} placeholder="e.g. Slot full hai, ye time better rahega"
              style={{...inputStyle, minHeight:'70px', resize:'vertical'}}/>
            <div style={{display:'flex', gap:'8px', marginTop:'14px', justifyContent:'flex-end'}}>
              <button onClick={()=>setCounterTarget(null)} style={{...btnStyle, backgroundColor:'#e5e7eb', color:'#333'}}>Cancel</button>
              <button onClick={submitCounter} disabled={busy} style={{...btnStyle, backgroundColor:theme.primary}}>
                {busy ? 'Sending…' : 'Send Counter-Proposal'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OnlineBookingsPage;
